/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useRef, useState } from "react";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { ROUTES } from "Router";
import { getEventId } from "Router/RoutesUtils";

import { useAuth } from "state";

import { Logo } from "components/icons";
import MetaTitle from "components/ui/MetaTitle";
import Stack from "components/ui/Stack";

import {
  BackupCodeAuth,
  ConfirmationCode,
  FAVerification,
  FAWelcome,
  LoginForm,
  RecoveryCodes,
  ResetPassword,
  ScanQrCode,
} from "./components/Forms";
import s from "./style.module.css";
import { LoginResponse } from "./types/login.types";
import LoadingScreen from "components/ui/LoadingScreen";

const LoginPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [currentForm, setCurrentForm] = useState("login");
  const [confirmationCode, setConfirmationCodes] = useState("");
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [username, setUsername] = useState("");

  const loginDataRef = useRef<LoginResponse | null>(null);

  const formsMapper = {
    login: (
      <LoginForm
        setCurrentForm={setCurrentForm}
        loginDataRef={loginDataRef}
        setUsername={setUsername}
      />
    ),
    otp_setup: <FAWelcome setCurrentForm={setCurrentForm} />,
    recovery_codes: <RecoveryCodes confirmationCode={confirmationCode} />,
    qr_code: <ScanQrCode setCurrentForm={setCurrentForm} />,
    reset_password: (
      <ResetPassword username={username} setCurrentForm={setCurrentForm} />
    ),
    otp_login: (
      <FAVerification
        setCurrentForm={setCurrentForm}
        loginDataRef={loginDataRef}
      />
    ),
    confirmation_code: (
      <ConfirmationCode
        setCurrentForm={setCurrentForm}
        setConfirmationCodes={setConfirmationCodes}
      />
    ),
    backup_code_auth: (
      <BackupCodeAuth
        setCurrentForm={setCurrentForm}
        loginDataRef={loginDataRef}
      />
    ),
  };

  useEffect(() => {
    const magicLinkToken = searchParams.get("token");

    if (magicLinkToken) {
      localStorage.setItem("token", magicLinkToken);
      setCurrentForm("reset_password");
    } else if (location?.state?.currentForm) {
      setCurrentForm(location.state.currentForm);
    } else {
      setCurrentForm("login");
    }
  }, [location.state, searchParams]);

  useEffect(() => {
    if (isLoggedIn) {
      const eventId = getEventId();
      if (eventId) {
        window.location.replace(ROUTES.HOME + `${eventId}`);
      } else {
        navigate(ROUTES.BASE);
      }
    }
  }, [isLoggedIn, navigate]);

  return isLoggedIn ? (
    <LoadingScreen />
  ) : (
    <>
      <MetaTitle title={`Login`} />

      <Stack className={s.root}>
        <div className="!mb-18">
          <Logo className={s.logo} />
        </div>
        {formsMapper[currentForm as keyof typeof formsMapper]}
      </Stack>
    </>
  );
};

export default LoginPage;
